import { FilterPanelBaseFilter } from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/BaseFilter";
import type { FilterValueDelta } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/misc";
import { calculateArrayDelta } from "app/pages/sales-intelligence/modules/advanced-search-filtering/utils/calculateValueDeltaUtils";
import type { ILeadsSearchDescriptionDto } from "app/pages/sales-intelligence/modules/leads-search-filters/types/description.dto";
import {
    createEmptyProfile,
    generateDummyNameForNextProfile,
    isProfileEnabled,
    isProfileNotEmpty,
    isSavedProfile,
} from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/common";
import type { Profile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/common";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";

import type { IContactsEmployeeProfiles } from "../../types/dto";

interface IOptions {
    profilesList: readonly ProfileDto[];
    seniority: readonly string[];
    departments: readonly string[];
}

class EmployeePersonasFilterModel extends FilterPanelBaseFilter<
    IContactsEmployeeProfiles,
    IOptions,
    readonly Profile[]
> {
    public currentsProfilesList: readonly ProfileDto[] = [];

    constructor(
        profilesList: readonly ProfileDto[],
        employeeProfileDetails: Partial<ILeadsSearchDescriptionDto["employeeProfile"]>,
    ) {
        const { seniority = [], departments = [] } = employeeProfileDetails;

        super({
            options: {
                profilesList,
                seniority,
                departments,
            },
        });

        this.currentsProfilesList = profilesList;
    }

    private getProfileIdList(profiles?: readonly Profile[]): readonly Profile["id"][] {
        if (!profiles) {
            return [];
        }

        return profiles.map(({ id }) => id);
    }

    getEnabledNotEmptyProfiles(profiles: readonly Profile[]): readonly Profile[] {
        return profiles.filter(isProfileNotEmpty).filter(isProfileEnabled);
    }

    fromApiValue(employeeProfiles?: IContactsEmployeeProfiles): readonly Profile[] {
        const { profileIds = [], profiles = [] } = (employeeProfiles || {}) as IContactsEmployeeProfiles;

        const profilesListLength = this.currentsProfilesList.length;

        if (profilesListLength === 0) {
            return [createEmptyProfile(generateDummyNameForNextProfile([]))];
        }

        const localProfiles: Profile[] = this.currentsProfilesList.map((profileItem) => {
            const { createdTime, id, ...apiProfileProps }: Omit<Profile, "name"> =
                profiles.find((item) => item.id === profileItem.id) || ({} as Omit<Profile, "name">);

            return {
                // @ts-ignore
                isExpanded: false,
                ...profileItem,
                ...apiProfileProps,
                isEnabled: profileIds.includes(profileItem.id),
            };
        });

        return localProfiles;
    }

    toApiValue(currentsProfilesList: readonly Profile[]): undefined | IContactsEmployeeProfiles {
        this.currentsProfilesList = currentsProfilesList;

        const savedEnabledNotEmptyProfiles =
            this.getEnabledNotEmptyProfiles(currentsProfilesList).filter(isSavedProfile);

        const profileIds = savedEnabledNotEmptyProfiles.map((profile) => profile.id);

        if (!savedEnabledNotEmptyProfiles.length) {
            return undefined;
        }

        return {
            profiles: savedEnabledNotEmptyProfiles,
            profileIds,
        };
    }

    isFilterDirty(currentValue: IContactsEmployeeProfiles | undefined): boolean {
        return Boolean(currentValue && Object.keys(currentValue).length > 0);
    }

    calculateValueDelta(
        previousValue: readonly Profile[] | undefined,
        newValue: readonly Profile[] | undefined,
    ): FilterValueDelta<Profile["id"]> {
        const prevProfileIdList = this.getProfileIdList(previousValue);
        const newProfileIdList = this.getProfileIdList(newValue);

        return calculateArrayDelta(prevProfileIdList, newProfileIdList);
    }
}

export default EmployeePersonasFilterModel;
